import { pushStorefrontDataToDatalayer } from '../../analytics/storefront'

// Action Type Constants
export const FETCH_STOREFRONT_PENDING = 'storefront//FETCH_STOREFRONT_PENDING'
export const FETCH_STOREFRONT_DONE = 'storefront//FETCH_STOREFRONT_DONE'
export const FETCH_STOREFRONT_ERROR = 'storefront//FETCH_STOREFRONT_ERROR'
export const STORE_STOREFRONTID = 'storefront//STORE_STOREFRONTID'
export const SET_STEP = 'storefront//SET_STEP'
export const SET_IS_LOADING_DATA = 'storefront//SET_IS_LOADING_DATA'

// Interfaces for State and Actions
interface CustomData {
  formSection?: string
  offer?: {
    validityEndDate?: string
  }
  expirationDate?: string
  agreeTermsAndConditionsCheck?: boolean
}

export interface StorefrontData {
  financialProduct: any
  token: string | null
  id: string | undefined
  transaction: any
  customerData: any
  data: any
  custom?: CustomData
  storefrontId?: string
}

interface StorefrontState {
  pending: boolean
  data: StorefrontData
  error: boolean | null
  isLoading: boolean
}

interface Action<T = any> {
  type: string
  payload?: T
}

// Action Creators
export function pending(): Action {
  return {
    type: FETCH_STOREFRONT_PENDING,
    payload: {},
  }
}

export function setIsLoadingData(loadingState: boolean): Action<boolean> {
  return {
    type: SET_IS_LOADING_DATA,
    payload: loadingState,
  }
}

export function done(data: StorefrontData): Action<StorefrontData> {
  pushStorefrontDataToDatalayer(data)
  return {
    type: FETCH_STOREFRONT_DONE,
    payload: data, // Matches the StorefrontData type
  }
}

export function getIsLoadingData(store: {
  storefront: StorefrontState
}): boolean {
  return store.storefront.isLoading
}

export function getStoredInReduxStorefrontData(store: {
  storefront: StorefrontState
}): StorefrontData {
  return store.storefront.data
}

export function getStep(store: {
  storefront: StorefrontState
}): string | undefined {
  return store.storefront.data.custom?.formSection
}

export function getOfferExpiration(store: {
  storefront: StorefrontState
}): string | undefined {
  return store.storefront.data.custom?.offer?.validityEndDate
}

export function getLinkExpiration(store: {
  storefront: StorefrontState
}): string | undefined {
  return store.storefront.data.custom?.expirationDate
}

export function error(err: Error): Action<Error> {
  return {
    type: FETCH_STOREFRONT_ERROR,
    payload: err,
  }
}

export function storeId(id: string): Action<string> {
  return {
    type: STORE_STOREFRONTID,
    payload: id,
  }
}

export function getStorefrontData(store: {
  storefront: StorefrontState
}): StorefrontData {
  return store.storefront.data
}

// Initial State
const initialState: StorefrontState = {
  pending: false,
  data: {
    customerData: undefined,
    data: undefined,
    transaction: undefined,
    id: undefined,
    token: null,
    financialProduct: undefined,
  }, // Initializes as an empty object matching StorefrontData
  error: null,
  isLoading: false,
}

// Reducer
export default function(state = initialState, action: Action): StorefrontState {
  switch (action.type) {
    case FETCH_STOREFRONT_PENDING:
      return {
        ...state,
        data: {
          customerData: undefined,
          data: undefined,
          transaction: undefined,
          id: undefined,
          token: null,
          financialProduct: undefined,
        },
        pending: true,
        error: null,
      }

    case FETCH_STOREFRONT_DONE:
      return {
        ...state,
        data: action.payload, // Updated to match StorefrontData
        pending: false,
        error: null,
      }

    case FETCH_STOREFRONT_ERROR:
      return {
        ...state,
        data: {
          customerData: undefined,
          data: undefined,
          transaction: undefined,
          id: undefined,
          token: null,
          financialProduct: undefined,
        },
        pending: false,
        error: true,
      }

    case STORE_STOREFRONTID:
      return {
        ...state,
        data: {
          ...state.data,
          storefrontId: action.payload, // Accesses payload directly
        },
      }

    case SET_IS_LOADING_DATA:
      return { ...state, isLoading: action.payload }

    default:
      return state
  }
}
